import {
    forwardRef,
    useEffect,
    useRef,
    useState,
    type ForwardRefExoticComponent,
    type PropsWithoutRef,
    type RefAttributes,
} from 'react';

import { useFrame, useThree, type ThreeElements } from '@react-three/fiber';
import * as THREE from 'three';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

import {
    useCameraInitialization,
    useCameraPersistence,
    usePanBounds,
    useRotationTracking,
    useTwoFingerGestureClassification,
    useViewOffset,
} from './hooks';

const defaultTargetTransitionEasing = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
};

type ForwardRefComponent<P, T> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;
type Overwrite<P, O> = Omit<P, keyof O> & O;

type OrbitControlsProps = Omit<
    Overwrite<
        Partial<OrbitControlsImpl> & ThreeElements['primitive'],
        {
            forcedAzimuthalAngle: number | null;
            forcedPolarAngle: number | null;
            enableDamping?: boolean;
            enablePan?: boolean;
            targetTransitionDuration?: number;
            horizontalOffset?: number;
            verticalOffset?: number;
            maxPanOffset?: number;
            panBoundsCenter?: THREE.Vector3;
            target?: THREE.Vector3;
            keyEvents?: boolean | HTMLElement;
            targetTransitionEasing?: (t: number) => number;
            onRotate?: () => void;
        }
    >,
    'ref'
>;

export const Camera: ForwardRefComponent<OrbitControlsProps, OrbitControlsImpl> = forwardRef<
    OrbitControlsImpl,
    OrbitControlsProps
>((props, ref) => {
    const {
        enableDamping = true,
        enablePan = true,
        keyEvents = false,
        target,
        forcedAzimuthalAngle,
        forcedPolarAngle,
        targetTransitionDuration = 1, // sec
        horizontalOffset = 0,
        verticalOffset = 0,
        panBoundsCenter,
        maxPanOffset = 1,
        targetTransitionEasing = defaultTargetTransitionEasing,
        onRotate,
        ...restProps
    } = props;

    const cameraTransitionRef = useRef({
        isTransitioning: false,
        progress: 0,
        startPosition: new THREE.Vector3(),
        endPosition: new THREE.Vector3(),
        currentPosition: new THREE.Vector3(),
    });
    // Track rotation changes (not zoom) to fire onRotate callback
    const previousAnglesRef = useRef({ azimuthal: 0, polar: 0 });
    const camera = useThree((state) => state.camera);
    const gl = useThree((state) => state.gl);
    const set = useThree((state) => state.set);
    // Reset camera up vector to standard Y-up when OrbitControls takes over
    // This is needed after IntrinsicCamera may have set a custom up vector
    useEffect(() => {
        camera.up.set(0, 1, 0);
    }, [camera]);

    const [controls] = useState<OrbitControlsImpl>(() => {
        const ctrls = new OrbitControlsImpl(camera);
        ctrls.enableDamping = true;
        ctrls.dampingFactor = 0.08;
        ctrls.enablePan = enablePan;

        // Prevent gimbal lock by constraining polar angle away from 0 and π
        const POLAR_EPSILON = 0.01;
        ctrls.minPolarAngle = POLAR_EPSILON;
        ctrls.maxPolarAngle = Math.PI - POLAR_EPSILON;

        // Register controls with threejs
        set({ controls: ctrls });

        return ctrls;
    });
    useCameraInitialization({
        camera,
        controls,
        controlTarget: target,
        panBoundsCenter,
        cameraTransitionRef,
        previousAnglesRef,
    });
    useTwoFingerGestureClassification({ controls, canvas: gl.domElement, enablePan });
    // This effect initializes the camera transition
    useEffect(() => {
        if (cameraTransitionRef.current.currentPosition.lengthSq() === 0) {
            if (target) {
                cameraTransitionRef.current.endPosition.copy(target);
                cameraTransitionRef.current.currentPosition.copy(target);
            } else if (panBoundsCenter) {
                // When no target but panBoundsCenter provided, use it as initial position
                // This ensures camera starts at a sensible height (waist level) instead of floor
                cameraTransitionRef.current.endPosition.copy(panBoundsCenter);
                cameraTransitionRef.current.currentPosition.copy(panBoundsCenter);
                controls.target.copy(panBoundsCenter);
            } else {
                cameraTransitionRef.current.endPosition.copy(controls.target);
                cameraTransitionRef.current.currentPosition.copy(controls.target);
            }
        }
    }, [controls, panBoundsCenter, target]);

    // This effect starts a new camera transition when `target` changes
    useEffect(() => {
        if (target && !target.equals(cameraTransitionRef.current.endPosition)) {
            cameraTransitionRef.current.isTransitioning = true;
            cameraTransitionRef.current.progress = 0;
            cameraTransitionRef.current.startPosition.copy(cameraTransitionRef.current.currentPosition);
            cameraTransitionRef.current.endPosition.copy(target);
        }
    }, [cameraTransitionRef, target]);

    // Sync enablePan prop with controls
    // When pan is disabled (focus mode), allow zoom + rotate but no pan
    useEffect(() => {
        controls.enablePan = enablePan;
        if (enablePan) {
            // Free mode: two fingers = dolly + pan (with gesture classification)
            controls.touches.TWO = THREE.TOUCH.DOLLY_PAN;
        } else {
            // Focus mode: two fingers = dolly + rotate (no pan)
            controls.touches.TWO = THREE.TOUCH.DOLLY_ROTATE;
        }
    }, [controls, enablePan]);

    usePanBounds({
        controls,
        enablePan,
        panBoundsCenter,
        maxPanOffset,
    });
    useViewOffset({ camera, canvas: gl.domElement, horizontalOffset, verticalOffset });
    useFrame((_, delta) => {
        // Only track focus target when one is provided
        // When no target, OrbitControls manages its own target (enabling free pan)
        if (target) {
            if (cameraTransitionRef.current.isTransitioning) {
                if (targetTransitionDuration <= 0) {
                    cameraTransitionRef.current.currentPosition.copy(cameraTransitionRef.current.endPosition);
                    cameraTransitionRef.current.isTransitioning = false;
                } else {
                    cameraTransitionRef.current.progress += delta / targetTransitionDuration;

                    const progress = Math.min(cameraTransitionRef.current.progress, 1);
                    const easedProgress = targetTransitionEasing(progress);

                    // Interpolate between start and end positions
                    cameraTransitionRef.current.currentPosition.lerpVectors(
                        cameraTransitionRef.current.startPosition,
                        cameraTransitionRef.current.endPosition,
                        easedProgress,
                    );

                    // End animation if complete
                    if (progress >= 1) {
                        cameraTransitionRef.current.isTransitioning = false;
                    }
                }
            }

            // Update controls target with the current animated position
            controls.target.copy(cameraTransitionRef.current.currentPosition);
        }

        if (typeof forcedAzimuthalAngle === 'number') controls.setAzimuthalAngle(forcedAzimuthalAngle);
        if (typeof forcedPolarAngle === 'number') controls.setPolarAngle(forcedPolarAngle);

        controls.update();
    }, -1);

    useEffect(() => {
        if (keyEvents) {
            controls.connect(keyEvents === true ? gl.domElement : keyEvents);
        }

        controls.connect(gl.domElement);
        return () => void controls.dispose();
    }, [keyEvents, gl, controls]);

    useCameraPersistence({ controls, enablePan });
    useRotationTracking({ controls, onRotate, previousAnglesRef });

    return <primitive ref={ref} object={controls} enableDamping={enableDamping} {...restProps} />;
});
